import React from "react"

import { ChevronDownIcon } from "@coval/ui"

import type { MetricRecordData } from "@/types/metrics/contracts"

import {
  FIELD_CONTAINER_STYLES,
  LABEL_STYLES,
  SELECT_STYLES,
  TEXT_INPUT_STYLES,
} from "../styles"
import { RequiredMarker } from "./RequiredMarker"

const VALID_OPERATORS = [
  { value: "<", label: "<" },
  { value: "<=", label: "≤" },
  { value: ">", label: ">" },
  { value: ">=", label: "≥" },
  { value: "==", label: "==" },
  { value: "!=", label: "!=" },
] as const

interface WordsPerMessageWithThresholdConfigProps {
  metricRecordData: MetricRecordData
  onChange: (metric: MetricRecordData) => void
}

export const WordsPerMessageWithThresholdConfig: React.FC<
  WordsPerMessageWithThresholdConfigProps
> = ({ metricRecordData, onChange }) => {
  const handleThresholdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10)
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        threshold: Number.isNaN(value) ? undefined : value,
      },
    })
  }

  const handleOperatorChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value as (typeof VALID_OPERATORS)[number]["value"]
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        operator: value,
      },
    })
  }

  return (
    <div className={FIELD_CONTAINER_STYLES}>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <label className={`${LABEL_STYLES} shrink-0`}>
          Word count
          <RequiredMarker />
        </label>
        <div className="relative w-16 shrink-0">
          <select
            className={`w-full ${SELECT_STYLES}`}
            value={metricRecordData.metric_metadata?.operator ?? ""}
            onChange={handleOperatorChange}
            aria-label="Operator"
          >
            <option value="">—</option>
            {VALID_OPERATORS.map((op) => (
              <option key={op.value} value={op.value}>
                {op.label}
              </option>
            ))}
          </select>
          <ChevronDownIcon className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2" />
        </div>
        <input
          type="number"
          min={0}
          step={1}
          className={`${TEXT_INPUT_STYLES} w-24 shrink-0`}
          value={metricRecordData.metric_metadata?.threshold ?? ""}
          onChange={handleThresholdChange}
          placeholder="e.g. 10"
          aria-label="Threshold"
        />
        <span className="text-xs text-coval-text-muted min-w-0">
          (YES if all agent messages pass)
        </span>
      </div>
    </div>
  )
}
